import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

const PricingPage = () => {
  const { isAuthenticated } = useAuth();
  const [billingCycle, setBillingCycle] = useState('monthly');

  const plans = {
    trial: {
      name: 'Free Trial',
      monthlyPrice: 0,
      yearlyPrice: 0,
      tokens: 250000,
      features: [
        '250,000 AI tokens included',
        'Basic genealogy research',
        'Document upload & analysis',
        'Chat with Gene AI',
        'Family tree insights',
        'Historical context',
        '14-day access period'
      ],
      limitations: [
        'Limited to 250,000 tokens total',
        'No overage tokens available',
        'Must upgrade to continue after trial'
      ],
      ctaText: 'Start Free Trial',
      ctaLink: '/register',
      popular: false
    },
    professional: {
      name: 'Professional',
      monthlyPrice: 100,
      yearlyPrice: 1044,
      tokens: 1000000,
      features: [
        '1,000,000 AI tokens per month',
        'Advanced genealogy research',
        'Unlimited document uploads',
        'Advanced chat with Gene AI',
        'Comprehensive family tree analysis',
        'Historical document insights',
        'DNA connection research',
        'Priority customer support',
        'Export research to PDF/Word',
        'Advanced search capabilities',
        'Collaboration tools',
        'Data backup & security'
      ],
      overage: {
        rate: 5.00,
        per: 100000,
        description: 'Additional tokens: $5.00 per 100,000 tokens'
      },
      ctaText: 'Subscribe Now',
      ctaLink: isAuthenticated ? '/account' : '/register',
      popular: true
    }
  };

  const calculateSavings = () => {
    const monthlyTotal = plans.professional.monthlyPrice * 12;
    const yearlyPrice = plans.professional.yearlyPrice;
    const savings = monthlyTotal - yearlyPrice;
    const percentage = Math.round((savings / monthlyTotal) * 100);
    return { savings, percentage };
  };

  const { savings, percentage } = calculateSavings();

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '2rem 0'
    }}>
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '0 2rem' 
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h1 style={{ 
            color: 'white', 
            fontSize: '3rem', 
            marginBottom: '1rem',
            fontWeight: 'bold'
          }}>
            Choose Your Plan
          </h1>
          <p style={{ 
            color: 'rgba(255, 255, 255, 0.9)', 
            fontSize: '1.2rem',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            Unlock the power of AI-driven genealogy research with Gene™. 
            Start your family history journey today.
          </p>
        </div>

        {/* Billing Toggle */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          marginBottom: '3rem' 
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            padding: '0.5rem',
            borderRadius: '50px',
            display: 'flex',
            gap: '0.5rem'
          }}>
            <button
              onClick={() => setBillingCycle('monthly')}
              style={{
                padding: '0.75rem 2rem',
                borderRadius: '25px',
                border: 'none',
                background: billingCycle === 'monthly' ? 'white' : 'transparent',
                color: billingCycle === 'monthly' ? '#333' : 'white',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              style={{
                padding: '0.75rem 2rem',
                borderRadius: '25px',
                border: 'none',
                background: billingCycle === 'yearly' ? 'white' : 'transparent',
                color: billingCycle === 'yearly' ? '#333' : 'white',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                position: 'relative'
              }}
            >
              Yearly
              <span style={{
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                background: '#4CAF50',
                color: 'white',
                fontSize: '0.7rem',
                padding: '2px 6px',
                borderRadius: '10px',
                fontWeight: 'bold'
              }}>
                Save {percentage}%
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
          gap: '2rem',
          marginBottom: '3rem'
        }}>
          {/* Trial Plan */}
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '2rem',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
            position: 'relative',
            border: '2px solid transparent'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <h3 style={{ 
                fontSize: '1.5rem', 
                marginBottom: '1rem',
                color: '#333'
              }}>
                {plans.trial.name}
              </h3>
              <div style={{ marginBottom: '1rem' }}>
                <span style={{ 
                  fontSize: '3rem', 
                  fontWeight: 'bold',
                  color: '#2e7d32'
                }}>
                  FREE
                </span>
              </div>
              <p style={{ color: '#666', fontSize: '0.9rem' }}>
                Perfect for getting started
              </p>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ 
                fontSize: '1.1rem', 
                marginBottom: '1rem',
                color: '#333',
                fontWeight: 'bold'
              }}>
                What's included:
              </h4>
              {plans.trial.features.map((feature, index) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  marginBottom: '0.5rem' 
                }}>
                  <span style={{ 
                    color: '#4CAF50', 
                    marginRight: '0.5rem',
                    fontSize: '1.2rem'
                  }}>
                    ✓
                  </span>
                  <span style={{ fontSize: '0.9rem', color: '#555' }}>
                    {feature}
                  </span>
                </div>
              ))}
            </div>

            <Link
              to={plans.trial.ctaLink}
              style={{
                display: 'block',
                textAlign: 'center',
                padding: '1rem 2rem',
                background: 'linear-gradient(135deg, #4CAF50, #45a049)',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '10px',
                fontWeight: 'bold',
                transition: 'all 0.3s ease'
              }}
            >
              {plans.trial.ctaText}
            </Link>
          </div>

          {/* Professional Plan */}
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '2rem',
            boxShadow: '0 15px 40px rgba(0, 0, 0, 0.3)',
            position: 'relative',
            border: '3px solid #FFD700',
            transform: 'scale(1.05)'
          }}>
            {/* Popular Badge */}
            <div style={{
              position: 'absolute',
              top: '-15px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'linear-gradient(135deg, #FFD700, #FFA000)',
              color: '#333',
              padding: '0.5rem 2rem',
              borderRadius: '25px',
              fontWeight: 'bold',
              fontSize: '0.9rem'
            }}>
              MOST POPULAR
            </div>

            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <h3 style={{ 
                fontSize: '1.5rem', 
                marginBottom: '1rem',
                color: '#333'
              }}>
                {plans.professional.name}
              </h3>
              <div style={{ marginBottom: '1rem' }}>
                <span style={{ 
                  fontSize: '3rem', 
                  fontWeight: 'bold',
                  color: '#2e7d32'
                }}>
                  ${billingCycle === 'monthly' ? plans.professional.monthlyPrice : Math.round(plans.professional.yearlyPrice / 12)}
                </span>
                <span style={{ 
                  fontSize: '1rem', 
                  color: '#666',
                  marginLeft: '0.5rem'
                }}>
                  /month
                </span>
              </div>
              {billingCycle === 'yearly' && (
                <div style={{
                  background: '#E8F5E8',
                  color: '#2e7d32',
                  padding: '0.5rem 1rem',
                  borderRadius: '20px',
                  fontSize: '0.9rem',
                  fontWeight: 'bold',
                  marginBottom: '1rem'
                }}>
                  Save ${savings}/year ({percentage}% off)
                </div>
              )}
              <p style={{ color: '#666', fontSize: '0.9rem' }}>
                {billingCycle === 'yearly' 
                  ? `Billed annually at $${plans.professional.yearlyPrice}` 
                  : `Billed monthly at $${plans.professional.monthlyPrice}`}
              </p>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ 
                fontSize: '1.1rem', 
                marginBottom: '1rem',
                color: '#333',
                fontWeight: 'bold'
              }}>
                Everything in Trial, plus:
              </h4>
              {plans.professional.features.map((feature, index) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  marginBottom: '0.5rem' 
                }}>
                  <span style={{ 
                    color: '#4CAF50', 
                    marginRight: '0.5rem',
                    fontSize: '1.2rem'
                  }}>
                    ✓
                  </span>
                  <span style={{ fontSize: '0.9rem', color: '#555' }}>
                    {feature}
                  </span>
                </div>
              ))}
            </div>

            {/* Overage Information */}
            <div style={{
              background: '#F5F5F5',
              padding: '1rem',
              borderRadius: '10px',
              marginBottom: '2rem'
            }}>
              <h5 style={{ 
                fontSize: '1rem', 
                marginBottom: '0.5rem',
                color: '#333'
              }}>
                💰 Overage Pricing
              </h5>
              <p style={{ 
                fontSize: '0.9rem', 
                color: '#666',
                marginBottom: '0.5rem'
              }}>
                {plans.professional.overage.description}
              </p>
              <p style={{ 
                fontSize: '0.8rem', 
                color: '#888'
              }}>
                Only charged when you exceed your monthly limit
              </p>
            </div>

            <Link
              to={plans.professional.ctaLink}
              style={{
                display: 'block',
                textAlign: 'center',
                padding: '1rem 2rem',
                background: 'linear-gradient(135deg, #2e7d32, #1b5e20)',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '10px',
                fontWeight: 'bold',
                fontSize: '1.1rem',
                transition: 'all 0.3s ease'
              }}
            >
              {plans.professional.ctaText}
            </Link>
          </div>
        </div>

        {/* Features Comparison */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '20px',
          padding: '2rem',
          marginBottom: '3rem'
        }}>
          <h3 style={{ 
            textAlign: 'center',
            fontSize: '2rem',
            marginBottom: '2rem',
            color: '#333'
          }}>
            Why Choose Gene Professional?
          </h3>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                fontSize: '3rem',
                marginBottom: '1rem'
              }}>
                🧬
              </div>
              <h4 style={{ 
                fontSize: '1.2rem',
                marginBottom: '1rem',
                color: '#333'
              }}>
                Advanced AI Research
              </h4>
              <p style={{ color: '#666' }}>
                Access to our most powerful genealogy AI with 1M monthly tokens for comprehensive family research.
              </p>
            </div>

            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                fontSize: '3rem',
                marginBottom: '1rem'
              }}>
                📄
              </div>
              <h4 style={{ 
                fontSize: '1.2rem',
                marginBottom: '1rem',
                color: '#333'
              }}>
                Unlimited Documents
              </h4>
              <p style={{ color: '#666' }}>
                Upload and analyze unlimited historical documents, photos, and records with advanced OCR technology.
              </p>
            </div>

            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                fontSize: '3rem',
                marginBottom: '1rem'
              }}>
                🎯
              </div>
              <h4 style={{ 
                fontSize: '1.2rem',
                marginBottom: '1rem',
                color: '#333'
              }}>
                Priority Support
              </h4>
              <p style={{ color: '#666' }}>
                Get expert help from our genealogy specialists and technical support team.
              </p>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '20px',
          padding: '2rem'
        }}>
          <h3 style={{ 
            textAlign: 'center',
            fontSize: '2rem',
            marginBottom: '2rem',
            color: '#333'
          }}>
            Frequently Asked Questions
          </h3>
          
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ 
                fontSize: '1.2rem',
                marginBottom: '0.5rem',
                color: '#333'
              }}>
                What happens when I use all my tokens?
              </h4>
              <p style={{ color: '#666' }}>
                <strong>Trial users:</strong> Must upgrade to Professional to continue.<br/>
                <strong>Professional users:</strong> Can purchase additional tokens at $5 per 100,000 tokens.
              </p>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ 
                fontSize: '1.2rem',
                marginBottom: '0.5rem',
                color: '#333'
              }}>
                Can I cancel anytime?
              </h4>
              <p style={{ color: '#666' }}>
                Yes! You can cancel your subscription at any time from your account settings. You'll continue to have access until your current billing period ends.
              </p>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ 
                fontSize: '1.2rem',
                marginBottom: '0.5rem',
                color: '#333'
              }}>
                Do unused tokens roll over?
              </h4>
              <p style={{ color: '#666' }}>
                Professional plan tokens reset monthly and do not roll over. We recommend using them throughout the month for the best genealogy research experience.
              </p>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ 
                fontSize: '1.2rem',
                marginBottom: '0.5rem',
                color: '#333'
              }}>
                Is my family data secure?
              </h4>
              <p style={{ color: '#666' }}>
                Absolutely. We use enterprise-grade encryption and security measures to protect your family history data. Your information is never shared with third parties.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
